import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Point, Area } from "react-easy-crop/types";
import { Dialog, DialogActions, DialogTitle, Slide, Typography, Button, Slider, Box} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Fonts } from "../types/Constants";
import { ImageOutlined } from "@mui/icons-material";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  // eslint-disable-next-line no-undef
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface UploadImageModalProps {
  open: boolean;
  onDeny?: (isOpen: boolean) => void;
  onConfirm: () => void;
  confirmButtonTitle?: string;
  denyButtonTitle?: string;
  disableDenyButton?: boolean;
  setCurrentAssetFile: (file: File | undefined) => void;
  setCurrentImageURL: React.Dispatch<React.SetStateAction<string>>;
  imageFile: File | undefined;
  imageURL: string;
  beaconModel?: string;
  cropShape?: "rect" | "round" | undefined;
  customAspectRatio?: number;
  paddingValue: number;
  crop: Point;
  setCrop: React.Dispatch<React.SetStateAction<Point>>;
  zoom: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
}

const UploadImageModal: React.FC<UploadImageModalProps> = ({
  open,
  onDeny,
  onConfirm,
  confirmButtonTitle = "Yes",
  denyButtonTitle = "No",
  disableDenyButton = false,
  setCurrentAssetFile,
  setCurrentImageURL,
  imageFile,
  imageURL,
  beaconModel,
  cropShape,
  customAspectRatio,
  paddingValue,
  crop,
  setCrop,
  zoom,
  setZoom,
}) => {

    const [croppedArea, setCroppedArea] = useState<Area>();
    const [paddedImageURL, setPaddedImageURL] = useState<string>("");

    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedArea(croppedAreaPixels);
    };

    // May need this in future for icon library
    // const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const inputFile = event.target.files![0];
    //     setCurrentAssetFile(inputFile);
    //     event.target.value = "";
    // };
    //
    // const clearFileField = () => {
    //     setCurrentImageURL("");
    //     setCurrentAssetFile(undefined);
    // };

    const padImage = (imageSrc: string, paddingRatio: number): Promise<{paddedUrl: string; zoomCorrection: number}> => {
      return new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => {

          const originalWidth = image.width;
          const originalHeight = image.height;

          const paddingX = originalWidth * paddingRatio;
          const paddingY = originalWidth * paddingRatio;
          const canvasWidth = originalWidth + paddingX * 2;
          const canvasHeight = originalHeight + paddingY * 2;

          const canvas = document.createElement("canvas");
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.drawImage(image, paddingX, paddingY, originalWidth, originalHeight);
          } else {
            return reject("Canvas context not available");
          }
          const zoomCorrection = Math.max(
            canvasWidth / originalWidth,
            canvasHeight / originalHeight
          );
          resolve({paddedUrl: canvas.toDataURL("image/png"), zoomCorrection});
        };
        image.onerror = reject;
        image.crossOrigin = "anonymous";
        image.src = imageSrc;
      });
    };

    useEffect(() => {
      if (imageURL) {
        // Add 5% padding to image
        padImage(imageURL, paddingValue)
          .then(({paddedUrl, zoomCorrection}) => {
            setPaddedImageURL(paddedUrl);
          })
          .catch(console.error);
      }
    }, [imageURL, paddingValue]);

    const getCroppedImage = () => {
      if (!croppedArea) return;
      const image = new Image();
      image.src = paddedImageURL ?? imageURL ?? "";

      const canvas = document.createElement("canvas");
      canvas.width = croppedArea.width;
      canvas.height = croppedArea.height;

      const ctx = canvas.getContext('2d');

      if (ctx) {
        
        if (croppedArea) {
          ctx.drawImage(
            image,
            croppedArea.x,
            croppedArea.y,
            croppedArea.width,
            croppedArea.height,
            0,
            0,
            croppedArea.width,
            croppedArea.height
          );
        }
      }

        canvas.toBlob(blob => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setCurrentImageURL(url);
            const file = new File([blob], imageFile?.name ?? "icon.png", {
              type: blob.type,
              lastModified: Date.now()
            });
            if (file) {
              setCurrentAssetFile(file);
            }
          }
        }, 'image/png');
    };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      fullWidth maxWidth={"sm"}
      onClose={() => onDeny && onDeny(false)}
    >
      <DialogTitle>
        <Typography
          sx={{
            mb: 3,
            fontWeight: Fonts.SEMI_BOLD,
          }}
          id="alert-dialog-title"
        >
          Image Upload (256 x 256)
        </Typography>
      </DialogTitle>
      <Box
        sx={{ color: "text.secondary", fontSize: 14, height: "500px", width: "100%", paddingX: 10}}
        id="alert-dialog-description"
      >
    <Box sx={{ display: "flex", flexDirection: "row", gap: 10, width: "100%", justifyContent: "center" }}>
        <Box sx={{ position: "relative", width: "500px", height: "500px" }}>
          {imageFile ? (
            <>
            <Box sx={{ position: "absolute", right: "10px", top: "10px", zIndex: 10 }}>
            {/* <IconButton sx={{ backgroundColor: "white", '&:hover': { backgroundColor: "white" }}} onClick={clearFileField}>
              <ClearIcon sx={{ fontSize: "20px"}} />
            </IconButton> */}
            </Box>
            <Box sx={{ position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: "100px",
                border: "1px solid black",
            }}>
                <Cropper
                image={paddedImageURL || imageURL}
                crop={crop}
                zoom={zoom}
                restrictPosition={paddingValue ? false : true}
                cropSize={
                  beaconModel === "billboardwide" || beaconModel === "billboardtall" || customAspectRatio
                  ? undefined
                  : { width: 256, height: 256 }
                }
                aspect={
                  customAspectRatio ? customAspectRatio : beaconModel === "billboardwide"
                    ? 16 / 9
                    : beaconModel === "billboardtall"
                    ? 4 / 5
                    : undefined
                }
                cropShape={
                  cropShape ? cropShape : beaconModel === "billboardwide" || beaconModel === "billboardtall" || beaconModel === "trisign"
                  ? "rect" : "round"
                 }
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                />
            </Box>
            </>
          ) : (
              <Box
                sx={{
                  cursor: 'pointer',
                  border: 'dashed 2px silver',
                  borderRadius: 2.5,
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                  backgroundColor: 'background.default',
                  alignContent: 'center',
                  width: 500,
                  height: 400,
                  position: "absolute"
                }}
              >
                {!imageFile && (
                  <>
                  <ImageOutlined
                    style={{
                      fontSize: 100,
                      color: 'silver',
                    }}
                  />
                </>
                )}
              </Box>
          )}    
            <Box sx={{ display: "flex", padding: "0px 80px", position: "relative", top: "410px", gap: 8 }}>
            <Slider 
                value={zoom}
                min={0.3}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e, zoom) => setZoom(Number(zoom))}
                classes={{ root: "slider" }}
            />
            <Typography>Zoom</Typography>
            </Box>
        </Box>
    </Box>
      </Box>
      <DialogActions
        sx={{
          pb: 5,
          px: 6,
        }}
      >
        {!disableDenyButton &&
          <Button
            variant="outlined"
            sx={{
              fontWeight: Fonts.MEDIUM,
              mr: 2
            }}
            onClick={() => onDeny && onDeny(false)}
            color="secondary"
          >
            {denyButtonTitle}
          </Button>
        }
        <Button
          variant="contained"
          sx={{
            fontWeight: Fonts.MEDIUM,
            backgroundColor: "primary",
            ":hover":{
              backgroundColor: "primary"
            }
            
          }}
          onClick={() => {
            getCroppedImage();
            onConfirm();
          }}
          autoFocus
        >
          {confirmButtonTitle}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadImageModal;
